/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  Autorenew as SyncIcon,
  GetApp as GetAppIcon,
} from '@material-ui/icons';
import Card from 'material-dashboard-components/Card/Card.js';
import CardHeader from 'material-dashboard-components/Card/CardHeader.js';
import CardIcon from 'material-dashboard-components/Card/CardIcon.js';
import GridContainer from 'material-dashboard-components/Grid/GridContainer.js';
import GridItem from 'material-dashboard-components/Grid/GridItem.js';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingProgress from 'common/components/progress/loading';
import { makeStyles } from '@material-ui/core/styles';
import PaymentLinkDetailModal from './components/PaymentLinkDetailModal';
import {
  getPaymentLinks,
  cancelPaymentLink,
  exportPaymentLinks,
  syncPaymentLinksStatuses,
  statusDoLink,
} from './paymentLinksActions';
import styles from 'material-dashboard-assets/jss/material-dashboard-react/views/dashboardStyle.js';

const useStyles = makeStyles(styles);

const customStyles = makeStyles((theme) => ({
  searchContainer: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  searchBar: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(0.5),
      backgroundColor: '#f8f9fa',
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
      '&.Mui-focused': {
        backgroundColor: 'white',
      },
    },
  },
  exportButton: {
    textTransform: 'none',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1, 3),
    fontWeight: 500,
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    '& .p-datatable': {
      fontSize: '13px',
      border: 'none',
    },
    '& .p-datatable-header': {
      background: 'transparent',
      borderBottom: '1px solid #e2e8f0',
      padding: theme.spacing(2),
    },
    '& .p-datatable-thead > tr > th': {
      backgroundColor: '#f8fafc',
      color: '#475569',
      fontWeight: 600,
      fontSize: '0.8rem',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      padding: theme.spacing(1.5, 1),
      borderColor: '#e2e8f0',
      whiteSpace: 'nowrap',
    },
    '& .p-datatable-tbody > tr > td': {
      padding: theme.spacing(1.25, 1),
      borderTop: '1px solid #f1f5f9',
      color: theme.palette.text.primary,
      fontSize: '0.9rem',
    },
    // Linhas em zebra
    '& .p-datatable-tbody > tr:nth-of-type(even)': {
      backgroundColor: '#f9fafb',
    },
    '& .p-datatable-tbody > tr:nth-of-type(odd)': {
      backgroundColor: '#ffffff',
    },
    // Hover
    '& .p-datatable-tbody > tr:hover': {
      backgroundColor: '#eef2ff !important',
    },
    '& .p-datatable-tbody > tr:hover td': {
      color: '#111827 !important',
    },
    // Estilos para linha selecionada
    '& .p-datatable-tbody > tr.p-highlight': {
      backgroundColor: '#e0f2fe !important',
      '& td': {
        color: '#0f172a !important',
      },
    },
    '& .p-datatable-tbody > tr.p-highlight:hover': {
      backgroundColor: '#bae6fd !important',
      '& td': {
        color: '#0f172a !important',
      },
    },
    // Paginator e dropdown de linhas por página
    '& .p-paginator': {
      borderTop: '1px solid #e2e8f0',
      padding: theme.spacing(0.5, 1.5),
      backgroundColor: '#ffffff',
    },
    '& .p-paginator .p-dropdown': {
      minWidth: 80,
      borderRadius: theme.spacing(2),
      border: '1px solid #cbd5f5',
      backgroundColor: '#f8fafc',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
      marginRight: theme.spacing(2.5),
    },
    '& .p-paginator .p-dropdown:hover': {
      borderColor: '#818cf8',
      backgroundColor: '#eef2ff',
    },
    '& .p-paginator .p-dropdown-label': {
      padding: theme.spacing(0.5, 1.5),
      fontSize: '0.85rem',
      color: '#111827',
    },
    '& .p-paginator .p-dropdown-trigger': {
      paddingRight: theme.spacing(1),
      color: '#6b7280',
    },
    '& .p-paginator .p-paginator-current': {
      marginLeft: theme.spacing(0.5),
    },
  },
  statusChip: {
    fontWeight: '500',
    fontSize: '0.75rem',
  },
  syncButton: {
    textTransform: 'none',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1, 3),
    fontWeight: 500,
    marginRight: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(0.5),
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '500',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
}));

const PaymentLinks = (props) => {
  const classes = useStyles();
  const customClasses = customStyles();
  const authUser = props.auth?.user;
  const stationId = authUser?.establishments?.[0]?.stations?.[0]?.id || null;
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: 'all',
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [linkToCancel, setLinkToCancel] = useState(null);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [currentPageFirst, setCurrentPageFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadPaymentLinks().catch(() => null);

    const intervalId = setInterval(() => {
      loadPaymentLinks({ silent: true }).catch(() => null);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [filters, stationId]);

  const loadPaymentLinks = async (options = {}) => {
    if (!options.silent) {
      setLoading(true);
    }

    if (!stationId) {
      if (!options.silent) {
        setLoading(false);
      }
      return null;
    }

    try {
      await props.getPaymentLinks(stationId, filters, options);
    } catch (error) {
      if (!options.silent) {
        console.error('Erro ao carregar links de pagamento:', error);
      }
      return null;
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPageFirst(0);
  };

  const handleViewDetails = (rowData) => {
    setSelectedLink(rowData);
    setDetailModalOpen(true);
  };

  const handleCancelLink = (linkId) => {
    setLinkToCancel(linkId);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (linkToCancel) {
      await props.cancelPaymentLink(linkToCancel);
      loadPaymentLinks();
    }
    setCancelDialogOpen(false);
    setLinkToCancel(null);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setLinkToCancel(null);
  };

  const handleExport = () => {
    const dataToExport = selectedLinks.length > 0 ? selectedLinks : props.paymentLinks.list;
    props.exportPaymentLinks(dataToExport);
  };

  const handleSyncPayments = async () => {
    setSyncingPayments(true);
    try {
      await props.syncPaymentLinksStatuses(currentPageLinks);
      await loadPaymentLinks({ silent: true });
    } finally {
      setSyncingPayments(false);
    }
  };

  // O mapa vive em paymentLinksActions: o modal e o chip desta lista precisam
  // dizer a mesma coisa sobre a mesma linha, e o recorte parcial que ficava aqui
  // exibia "Pendente" para link recusado, expirado por engano ou inadimplente.
  const statusBodyTemplate = (rowData) => {
    const config = statusDoLink(rowData.status);

    return (
      <Chip
        label={config.label}
        size="small"
        style={config.style}
        className={customClasses.statusChip}
      />
    );
  };

  const valueBodyTemplate = (rowData) => {
    return (
      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
        R$ {parseFloat(rowData.value).toFixed(2).replace('.', ',')}
      </Typography>
    );
  };

  const nfBodyTemplate = (rowData) => {
    return <Checkbox checked={rowData.hasInvoice} disabled color="primary" />;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <Box display="flex" alignItems="center">
        <Tooltip title="Ver detalhes">
          <IconButton size="small" color="primary" onClick={() => handleViewDetails(rowData)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {rowData.status === 'pending' && (
          <Tooltip title="Cancelar link">
            <IconButton
              size="small"
              style={{ color: '#d32f2f' }}
              onClick={() => handleCancelLink(rowData.id)}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const dateBodyTemplate = (rowData) => {
    const date = new Date(rowData.createdAt);
    return date.toLocaleDateString('pt-BR');
  };

  // Estatísticas - usar métricas da API ou calcular localmente
  const metrics = props.paymentLinks?.metrics || {};
  const stats = {
    paidLinks: metrics.paidCount ?? props.paymentLinks?.list?.filter((l) => l.status === 'paid').length ?? 0,
    activeActivities: metrics.activeCount ?? props.paymentLinks?.list?.filter((l) => l.status === 'pending').length ?? 0,
    netValue: metrics.netValue ?? props.paymentLinks?.list
        ?.filter((l) => l.status === 'paid')
        .reduce((sum, l) => sum + (l.value - (l.fee || 0)), 0) ?? 0,
    stationValue: metrics.stationNetValue ?? props.paymentLinks?.list
        ?.filter((l) => l.status === 'paid')
        .reduce((sum, l) => sum + (l.stationValue || 0), 0) ?? 0,
  };

  const filteredData =
    props.paymentLinks?.list?.filter((link) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        link.name?.toLowerCase().includes(searchLower) ||
        link.cpf?.includes(searchTerm) ||
        link.activity?.toLowerCase().includes(searchLower) ||
        link.service?.toLowerCase().includes(searchLower)
      );
    }) || [];

  const effectivePageFirst = currentPageFirst >= filteredData.length ? 0 : currentPageFirst;
  const currentPageLinks = filteredData.slice(effectivePageFirst, effectivePageFirst + rowsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingProgress />
      </Box>
    );
  }

  return (
    <>
      <section id="payment-links">
        <div style={{ maxWidth: 1280 }}>
          <GridContainer>
            <GridItem xs={12} sm={6} md={3}>
              <Card style={{ position: 'relative' }} className="active-actions">
                <CardHeader color="primary" stats icon>
                  <CardIcon color="white">
                    <Icon color="primary">check_circle</Icon>
                  </CardIcon>
                  <p className={classes.cardCategory}>Vendas pagas (todo o período)</p>
                  <h3 className={classes.cardTitle}>{stats.paidLinks}</h3>
                </CardHeader>
              </Card>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Card style={{ position: 'relative' }} className="active-actions">
                <CardHeader color="primary" stats icon>
                  <CardIcon color="white">
                    <Icon color="primary">link</Icon>
                  </CardIcon>
                  <p className={classes.cardCategory}>Atividades com link ativos</p>
                  <h3 className={classes.cardTitle}>{stats.activeActivities}</h3>
                </CardHeader>
              </Card>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Card style={{ position: 'relative' }} className="active-actions">
                <CardHeader color="primary" stats icon>
                  <CardIcon color="white">
                    <Icon color="primary">attach_money</Icon>
                  </CardIcon>
                  <p className={classes.cardCategory}>Valor Líquido</p>
                  <h3 className={classes.cardTitle}>
                    R$ {stats.netValue.toFixed(2).replace('.', ',')}
                  </h3>
                </CardHeader>
              </Card>
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <Card style={{ position: 'relative' }} className="active-actions">
                <CardHeader color="primary" stats icon>
                  <CardIcon color="white">
                    <Icon color="primary">hourglass_empty</Icon>
                  </CardIcon>
                  <p className={classes.cardCategory}>Valor destinado à estação</p>
                  <h3 className={classes.cardTitle}>
                    R$ {stats.stationValue.toFixed(2).replace('.', ',')}
                  </h3>
                </CardHeader>
              </Card>
            </GridItem>
          </GridContainer>

          {/* Barra de Pesquisa e Ações */}
          <Box className={customClasses.searchContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Buscar por nome, CPF, atividade ou serviço..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className={customClasses.searchBar}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SyncIcon />}
                    onClick={handleSyncPayments}
                    disabled={syncingPayments || loading}
                    className={customClasses.syncButton}>
                    {syncingPayments ? 'SINCRONIZANDO...' : 'SYNC'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleExport}
                    className={customClasses.exportButton}>
                    EXPORTAR EXCEL
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Tabela de Links */}
          <Box className={customClasses.tableContainer}>
            <DataTable
              value={filteredData}
              paginator
              first={effectivePageFirst}
              rows={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onPage={(e) => {
                setCurrentPageFirst(e.first);
                setRowsPerPage(e.rows);
              }}
              selection={selectedLinks}
              onSelectionChange={(e) => setSelectedLinks(e.value)}
              dataKey="id"
              emptyMessage="Nenhum link de pagamento encontrado"
              currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} links"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown">
              <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
              <Column field="status" header="Status" body={statusBodyTemplate} sortable />
              <Column field="id" header="ID do Link" sortable />
              <Column field="activity" header="Atividade" sortable />
              <Column field="createdAt" header="Data" body={dateBodyTemplate} sortable />
              <Column field="name" header="Nome" sortable />
              <Column field="cpf" header="CPF" sortable />
              <Column field="value" header="Valor" body={valueBodyTemplate} sortable />
              <Column field="service" header="Serviço" sortable />
              <Column field="hasInvoice" header="NF" body={nfBodyTemplate} />
              <Column header="Ações" body={actionsBodyTemplate} />
            </DataTable>
          </Box>

          {/* Modal de Detalhes */}
          {detailModalOpen && (
            <PaymentLinkDetailModal
              open={detailModalOpen}
              onClose={() => setDetailModalOpen(false)}
              link={selectedLink}
              onRefresh={loadPaymentLinks}
            />
          )}

          {/* Modal de Confirmação de Cancelamento */}
          <Dialog
            open={cancelDialogOpen}
            onClose={handleCloseCancelDialog}
            aria-labelledby="cancel-dialog-title"
            aria-describedby="cancel-dialog-description">
            <DialogTitle id="cancel-dialog-title">Cancelar Link de Pagamento</DialogTitle>
            <DialogContent>
              <DialogContentText id="cancel-dialog-description">
                Tem certeza que deseja cancelar este link de pagamento? Esta ação não pode ser
                desfeita.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCancelDialog} color="default">
                Não, manter
              </Button>
              <Button
                onClick={handleConfirmCancel}
                variant="contained"
                style={{ backgroundColor: '#d32f2f', color: '#ffffff' }}>
                Sim, cancelar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </section>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  paymentLinks: state.paymentLinks,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getPaymentLinks,
      cancelPaymentLink,
      exportPaymentLinks,
      syncPaymentLinksStatuses,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(PaymentLinks);
